// src/pages/staff-management/index.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
// import QuickActionButton from "../../components/ui/QuickActionButton";

import StaffTable from "./components/StaffTable";
import StaffCard from "./components/StaffCard";
import StaffForm from "./components/StaffForm";
import TimeOffManagement from "./components/TimeOffManagement";
import StaffStats from "./components/StaffStats";
import OverlayListModal from "../../components/ui/OverlayListModal";

import api from "../../lib/api";
import { useAuth } from "../../auth/AuthProvider";
import { listServices } from "../../lib/services";
import { listServiceCategories } from "../../lib/serviceCategories";
import { listTimeOff, createTimeOff, patchTimeOff, deleteTimeOff } from "../../lib/timeOff";

const StaffManagement = () => {
  const { t } = useTranslation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState("staff");
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState(null);

  // lista de servicii disponibile pentru business (folosite în StaffForm)
  const [services, setServices] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);

  // modal listă staff (click pe "Total angajați")
  const [showStaffListModal, setShowStaffListModal] = useState(false);

  const [showAllStaffModal, setShowAllStaffModal] = useState(false);

  const { currentBusinessId, permissions } = useAuth();


  // Time-off requests (real backend)
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [timeOffLoading, setTimeOffLoading] = useState(false);
  const [timeOffError, setTimeOffError] = useState(null);

  const canSeeDashboard = Boolean(permissions?.canSeeDashboard);
  const canSeeStats = Boolean(permissions?.canSeeStats);
  const breadcrumbItems = canSeeDashboard
    ? [
        { label: t("nav.dashboard"), path: "/app/admin-dashboard" },
        { label: t("staff.pageTitle"), path: "/app/staff-management" },
      ]
    : [{ label: t("staff.pageTitle"), path: "/app/staff-management" }];

  // Roluri afișate în UI (nu codurile interne)
  const roleOptions = [
    { value: "all", label: t("staff.filterAllRoles") },
    { value: "owner", label: t("staff.roleLabels.owner") },
    { value: "director", label: t("staff.roleLabels.director") },
    { value: "reception", label: t("staff.roleLabels.reception") },
    { value: "employee", label: t("staff.roleLabels.employee") },
  ];

  const statusOptions = [
    { value: "all", label: t("staff.filterAllStatuses") },
    { value: "active", label: t("common.active") },
    { value: "inactive", label: t("common.inactive") },
    { value: "on_leave", label: t("staff.onLeave") },
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // helper: map cod rol → label prietenos
  const mapPrimaryRoleLabel = (item) => {
    // dacă e owner pe business
    if (item.is_owner) {
      return t("staff.roleOwner");
    }

    const locRoles = item.roles?.locations || [];
    const firstRole = locRoles[0]?.role;

    switch (firstRole) {
      case "director":
        return t("staff.roleDirectorBranch");
      case "reception":
        return t("staff.roleReception");
      case "employee":
        return t("staff.roleEmployee");
      default:
        return t("staff.roleEmployee");
    }
  };

  // ---------- Fetch staff din backend ----------

  const fetchStaff = useCallback(async () => {
    const bid =
      currentBusinessId || localStorage.getItem("current_business_id");
    if (!bid) {
      setStaff([]);
      return;
    }

    setStaffLoading(true);
    setStaffError(null);

    try {
      const { data } = await api.get("/b/staff", {
        headers: { "X-Business-Id": bid },
      });

      const list = Array.isArray(data) ? data : [];

      const mapped = list.map((item) => {
        const fullName = `${(item.first_name || "").trim()} ${(item.last_name || "").trim()}`.trim();

        const locRoles = item.roles?.locations || [];
        const primaryRoleCode = item.is_owner
          ? "owner"
          : locRoles[0]?.role || "employee";

        return {
          id: item.id,
          name: fullName || item.email,
          username: item.username || "",
          email: item.email,
          phone: item.phone || "",
          // label frumos pentru UI (tabel, card, filtre)
          role: mapPrimaryRoleLabel(item),
          // cod intern pentru backend (director / reception / employee)
          roleCode: primaryRoleCode,
          status:
            item.status ||
            (item.is_active === false ? "inactive" : "active"),
          // avatar poate veni ca avatar_url sau avatar direct
          avatar: item.avatar_url || item.avatar || "",
          // ⚠️ lista de ID-uri de servicii (ManyToMany)
          services: item.services || [],
          // filtrul de categorii al angajatului (persistat in DB)
          serviceCategoryFilters: item.service_category_filters || [],
          // programul de lucru – JSON din backend
          workingHours: item.working_hours || item.workingHours || {},
          _raw: item,
        };
      });

      setStaff(mapped);
    } catch (e) {
      console.error(
        "Eroare la încărcarea angajaților:",
        e?.response?.data || e
      );
      setStaffError("Nu s-au putut încărca angajații pentru acest business.");
    } finally {
      setStaffLoading(false);
    }
  }, [currentBusinessId]);

  // ---------- Fetch time-off din backend ----------
  const fetchTimeOff = useCallback(async () => {
    const bid =
      currentBusinessId || localStorage.getItem("current_business_id");
    if (!bid) {
      setTimeOffRequests([]);
      return;
    }

    setTimeOffLoading(true);
    setTimeOffError(null);

    try {
      const list = await listTimeOff();
      const arr = Array.isArray(list) ? list : [];

      const mapped = arr.map((r) => ({
        id: r.id,
        staffId: r.staff_id || r.staff || r.staff_user,
        staffName: r.staff_name || "",
        staffAvatar: r.staff_avatar_url || "",
        type: r.type || "vacation",
        startDate: r.start_date || (r.start_at ? String(r.start_at).slice(0, 10) : ""),
        endDate: r.end_date || (r.end_at ? String(r.end_at).slice(0, 10) : ""),
        requestDate:
          r.request_date || (r.created_at ? String(r.created_at).slice(0, 10) : ""),
        status: r.status || "pending",
        reason: r.reason || "",
        isPaid: r.is_paid !== false,
        _raw: r,
      }));

      setTimeOffRequests(mapped);
    } catch (e) {
      console.error("Eroare la incarcarea time-off:", e?.response?.data || e);
      setTimeOffError("Nu s-au putut incarca cererile de concediu.");
      setTimeOffRequests([]);
    } finally {
      setTimeOffLoading(false);
    }
  }, [currentBusinessId]);

  const isStaffOnLeaveNow = useCallback(
    (staffId) => {
      if (!staffId) return false;
      const today = new Date();
      const t0 = new Date(today);
      t0.setHours(0, 0, 0, 0);

      return (timeOffRequests || []).some((r) => {
        if (!r || r.status !== "approved") return false;
        if (String(r.staffId) !== String(staffId)) return false;
        if (!r.startDate || !r.endDate) return false;

        const s = new Date(r.startDate);
        s.setHours(0, 0, 0, 0);
        const e = new Date(r.endDate);
        e.setHours(23, 59, 59, 999);

        return t0 >= s && t0 <= e;
      });
    },
    [timeOffRequests]
  );

  const getEffectiveStaffStatus = useCallback(
    (member) => {
      if (!member) return "active";
      if (isStaffOnLeaveNow(member.id)) return "on_leave";
      return member.status;
    },
    [isStaffOnLeaveNow]
  );

  // ---------- Fetch categorii servicii (pentru filtrare în StaffForm) ----------

  useEffect(() => {
    let alive = true;

    listServiceCategories()
      .then((data) => {
        if (!alive) return;
        const arr = Array.isArray(data) ? data : [];
        setServiceCategories(arr);
      })
      .catch((e) => {
        if (!alive) return;
        console.error("Nu s-au putut încărca categoriile de servicii:", e);
        setServiceCategories([]);
      });

    return () => {
      alive = false;
    };
  }, [currentBusinessId]);

  useEffect(() => {
    fetchStaff();
    fetchTimeOff();
  }, [fetchStaff, fetchTimeOff]);

  // ---------- Fetch servicii din backend (pentru StaffForm) ----------

  useEffect(() => {
    let alive = true;

    listServices()
      .then((data) => {
        if (!alive) return;
        const arr = Array.isArray(data) ? data : [];

        const options = arr
          .map((svc) => {
            const isActive =
              typeof svc.active === "boolean"
                ? svc.active
                : typeof svc.is_active === "boolean"
                ? svc.is_active
                : true;

            return {
              id: svc.id,
              name: svc.name,
              active: isActive,
              category: (svc.category || "").toString().trim(),
            };
          })
          // în formular arătăm doar serviciile active, cu id + nume valid
          .filter((svc) => svc.id && svc.name && svc.active);

        setServices(options);
      })
      .catch((e) => {
        if (!alive) return;
        console.error("Nu s-au putut încărca serviciile pentru staff:", e);
      });

    return () => {
      alive = false;
    };
  }, [currentBusinessId]);



  const effectiveStaff = (staff || []).map((m) => ({
    ...m,
    status: getEffectiveStaffStatus(m),
  }));

  const filteredStaff = effectiveStaff.filter((member) => {
    const matchesSearch =
      member?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      member?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    const matchesRole = filterRole === "all" || member?.roleCode === filterRole;
    const matchesStatus =
      filterStatus === "all" || member?.status === filterStatus;

    return matchesSearch && matchesRole && matchesStatus;
  });

  // ---------- Save / update staff (cu avatar + program de lucru + servicii) ----------

  const handleSaveStaff = async (staffData) => {
    const bid =
      currentBusinessId || localStorage.getItem("current_business_id");
    if (!bid) {
      alert("Nu este selectat niciun business.");
      return;
    }

    const fullName =
      staffData.name ||
      `${staffData.firstName || staffData.first_name || ""} ${
        staffData.lastName || staffData.last_name || ""
      }`.trim();

    let firstName = staffData.firstName || staffData.first_name;
    let lastName = staffData.lastName || staffData.last_name;

    if (!firstName && fullName) {
      const parts = fullName.split(" ");
      firstName = parts[0];
      lastName = parts.slice(1).join(" ");
    }

    // validare telefon
    if (staffData.phone && staffData.phone.trim()) {
      const digitsOnly = staffData.phone.replace(/\D/g, "");
      if (digitsOnly.length < 6 || digitsOnly.length > 15) {
        alert("Numărul de telefon trebuie să aibă între 6 și 15 cifre.");
        return;
      }
    }

    // trimitem multipart: avatar + working_hours + services
    const formData = new FormData();
    formData.append("first_name", firstName || "");
    formData.append("last_name", lastName || "");
    formData.append("email", staffData.email);
    formData.append("phone", staffData.phone || "");
    formData.append("role", staffData.role || "employee");

    // credentials:
    //  - Create: username + password obligatorii
    //  - Edit: parola nouă e opțională (reset), username rămâne neschimbat
    if (editingStaff && editingStaff.id) {
      if (staffData.password) formData.append("password", staffData.password);
    } else {
      formData.append("username", staffData.username || "");
      formData.append("password", staffData.password || "");
    }
    if (staffData.status) {
      formData.append("status", staffData.status);
    }

    if (staffData.workingHours) {
      formData.append(
        "working_hours",
        JSON.stringify(staffData.workingHours)
      );
    }

    // aici trimitem lista de ID-uri de servicii (ManyToMany)
    if (staffData.services && staffData.services.length > 0) {
      formData.append("services", JSON.stringify(staffData.services));
    }

    // filtrul de categorii selectat la angajat (persistat in DB)
    if ("service_category_filters" in staffData || "serviceCategoryFilters" in staffData) {
      const raw = staffData.service_category_filters ?? staffData.serviceCategoryFilters ?? [];
      formData.append("service_category_filters", JSON.stringify(Array.isArray(raw) ? raw : []));
    }

    if (staffData.avatarFile) {
      formData.append("avatar", staffData.avatarFile);
    }

    try {
      if (editingStaff && editingStaff.id) {
        await api.patch(`/b/staff/${editingStaff.id}`, formData, {
          headers: {
            "X-Business-Id": bid,
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        await api.post("/b/staff", formData, {
          headers: {
            "X-Business-Id": bid,
            "Content-Type": "multipart/form-data",
          },
        });
      }

      await fetchStaff();
      setShowStaffForm(false);
      setEditingStaff(null);
    } catch (e) {
      console.error("Eroare la salvarea angajatului:", e?.response?.data || e);
      const resp = e?.response?.data;
      let msg = "Nu s-a putut salva angajatul.";
      if (typeof resp === "string") {
        msg = resp;
      } else if (resp?.detail) {
        msg = resp.detail;
      } else if (resp && typeof resp === "object") {
        const firstKey = Object.keys(resp)[0];
        if (firstKey) {
          const val = resp[firstKey];
          msg = `${firstKey}: ${Array.isArray(val) ? val[0] : String(val)}`;
        }
      }
      alert(msg);
    }
  };

  const handleCreateStaff = () => {
    setEditingStaff(null);
    setShowStaffForm(true);
  };

  const handleEditStaff = (member) => {
    // Trimitem către formular:
    //  - name, email, phone => ca să apară datele de contact
    //  - roleCode => ca rolul selectat să corespundă opțiunilor din dropdown
    setEditingStaff({
      ...member,
      role: member.roleCode || "employee",
    });
    setShowStaffForm(true);
  };

  const handleDeleteStaff = async (member) => {
    if (
      !window.confirm(
        `Sunteți sigur că doriți să ștergeți angajatul ${member?.name}?`
      )
    ) {
      return;
    }

    const bid =
      currentBusinessId || localStorage.getItem("current_business_id");
    if (!bid) {
      alert("Nu este selectat niciun business.");
      return;
    }

    try {
      await api.delete(`/b/staff/${member.id}`, {
        headers: { "X-Business-Id": bid },
      });
      await fetchStaff();
    } catch (e) {
      console.error(
        "Eroare la ștergerea angajatului:",
        e?.response?.data || e
      );

      const resp = e?.response?.data;
      let msg = "Nu s-a putut șterge angajatul.";

      if (typeof resp === "string") {
        msg = resp;
      } else if (resp?.detail) {
        msg = resp.detail;
      } else if (resp && typeof resp === "object") {
        const firstKey = Object.keys(resp)[0];
        if (firstKey) {
          const val = resp[firstKey];
          msg = Array.isArray(val) ? val[0] : String(val);
        }
      }

      alert(msg);
    }
  };

  const handleViewSchedule = (member) => {
    window.location.href = `/appointment-calendar?staff=${member?.id}`;
  };

  const handleEditAvailability = (member) => {
    setEditingStaff({
      ...member,
      role: member.roleCode || "employee",
    });
    setShowStaffForm(true);
  };

  const handleApproveTimeOff = async (request) => {
    try {
      if (!request?.id) return;
      await patchTimeOff(request.id, { status: "approved" });
      await fetchTimeOff();
    } catch (e) {
      console.error("Eroare la aprobarea cererii:", e?.response?.data || e);
      const resp = e?.response?.data;
      const msg = resp?.detail || "Nu s-a putut aproba cererea.";
      alert(msg);
    }
  };

  const handleRejectTimeOff = async (request) => {
    try {
      if (!request?.id) return;
      await patchTimeOff(request.id, { status: "rejected" });
      await fetchTimeOff();
    } catch (e) {
      console.error("Eroare la respingerea cererii:", e?.response?.data || e);
      const resp = e?.response?.data;
      const msg = resp?.detail || "Nu s-a putut respinge cererea.";
      alert(msg);
    }
  };

  const handleCreateTimeOff = async (payload) => {
    try {
      await createTimeOff(payload);
      await fetchTimeOff();
    } catch (e) {
      console.error("Eroare la crearea cererii:", e?.response?.data || e);
      const resp = e?.response?.data;
      let msg = resp?.detail || "Nu s-a putut crea cererea.";
      if (!resp?.detail && resp && typeof resp === "object") {
        const k = Object.keys(resp)[0];
        if (k) {
          const v = resp[k];
          msg = Array.isArray(v) ? v[0] : String(v);
        }
      }
      alert(msg);
      throw e;
    }
  };

  const handleViewTimeOffDetails = (request) => {
    alert(
      `Detalii cerere concediu:\n\nAngajat: ${request?.staffName}\nTip: ${
        request?.type
      }\nPerioadă: ${request?.startDate} - ${request?.endDate}\nMotiv: ${
        request?.reason
      }`
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        isCollapsed={isSidebarCollapsed}
        onToggleSidebar={handleToggleSidebar}
      />
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={handleToggleSidebar}
      />
      <main
        className={`page-top pb-20 lg:pb-6 transition-all duration-300 ${
          isSidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-6">
          <div className="mb-6">
            <Breadcrumbs items={breadcrumbItems} />

            {/* ======= PAGE HEADER ======= */}
            <div className="mt-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground">
                  {t("staff.pageTitle")}
                </h1>
                <p className="text-text-secondary mt-1">
                  {t("staff.pageSubtitle")}
                </p>

                {/* Mobil: butonul sub text, full width */}
                <div className="mt-4 w-full lg:hidden">
                  <Button
                    variant="default"
                    onClick={handleCreateStaff}
                    className="w-full justify-center"
                  >
                    <Icon name="Plus" size={20} />
                    <span>{t("staff.newStaff")}</span>
                  </Button>
                </div>
              </div>

              {/* Desktop / tablet: butonul în dreapta, fără space-x pe mobil */}
              <div className="hidden lg:flex">
                <Button
                  variant="default"
                  onClick={handleCreateStaff}
                  className="flex items-center gap-2"
                >
                  <Icon name="Plus" size={20} />
                  <span>{t("staff.newStaff")}</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center gap-1 mb-6 bg-muted rounded-lg p-1 overflow-x-auto">
            <Button
              variant={activeTab === "staff" ? "default" : "ghost"}
              onClick={() => setActiveTab("staff")}
              className="flex items-center gap-1.5 flex-1 justify-center min-w-0 whitespace-nowrap"
            >
              <Icon name="Users" size={16} className="shrink-0" />
              <span className="truncate">{t("staff.tabStaff")}</span>
            </Button>
            <Button
              variant={activeTab === "timeoff" ? "default" : "ghost"}
              onClick={() => setActiveTab("timeoff")}
              className="flex items-center gap-1.5 flex-1 justify-center min-w-0 whitespace-nowrap"
            >
              <Icon name="Calendar" size={16} className="shrink-0" />
              <span className="truncate">{t("staff.tabTimeOff")}</span>
              {timeOffRequests?.filter((r) => r?.status === "pending" && r?.type !== "day_off")
                ?.length > 0 && (
                <span className="bg-error text-error-foreground text-xs px-1.5 py-0.5 rounded-full shrink-0">
                  {timeOffRequests.filter((r) => r?.status === "pending" && r?.type !== "day_off").length}
                </span>
              )}
            </Button>
            <Button
              variant={activeTab === "dayoff" ? "default" : "ghost"}
              onClick={() => setActiveTab("dayoff")}
              className="flex items-center gap-1.5 flex-1 justify-center min-w-0 whitespace-nowrap"
            >
              <Icon name="Sun" size={16} className="shrink-0" />
              <span className="truncate">{t("staff.tabDayOff")}</span>
              {timeOffRequests?.filter((r) => r?.status === "pending" && r?.type === "day_off")
                ?.length > 0 && (
                <span className="bg-error text-error-foreground text-xs px-1.5 py-0.5 rounded-full shrink-0">
                  {timeOffRequests.filter((r) => r?.status === "pending" && r?.type === "day_off").length}
                </span>
              )}
            </Button>
          </div>

          {activeTab === "staff" && (
            <>
              {canSeeStats && (
                <StaffStats
                  staff={effectiveStaff}
                  onOpenTotal={() => setShowStaffListModal(true)}
                />
              )}

              {/* Filters and Search */}
              <div className="bg-background rounded-lg border border-border p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Input
                    type="search"
                    placeholder={t("staff.searchStaffPlaceholder")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    className="md:col-span-2"
                  />
                  <Select
                    options={roleOptions}
                    value={filterRole}
                    onChange={setFilterRole}
                    placeholder="Filtrare rol"
                  />
                  <Select
                    options={statusOptions}
                    value={filterStatus}
                    onChange={setFilterStatus}
                    placeholder="Filtrare status"
                  />
                </div>
              </div>

              {/* Staff Form */}
              {showStaffForm && (
                <div className="mb-6">
                  <StaffForm
                    staff={editingStaff}
                    onSave={handleSaveStaff}
                    onCancel={() => {
                      setShowStaffForm(false);
                      setEditingStaff(null);
                    }}
                    isEditing={!!editingStaff}
                    // 👇 aici trecem lista de servicii din backend
                    serviceOptions={services}
                    categoryOptions={serviceCategories}
                  />
                </div>
              )}

              {/* Lista angajați */}
              {staffLoading && (
                <div className="bg-background rounded-lg border border-border p-6 mb-4 text-center text-sm text-text-secondary">
                  Se încarcă angajații pentru acest business...
                </div>
              )}

              {staffError && (
                <div className="bg-error/10 border border-error/40 text-error text-sm rounded-lg p-4 mb-4">
                  {staffError}
                </div>
              )}

              {!staffLoading && !staffError && (
                <>
                  {isMobile ? (
                    <div className="space-y-4">
                      {filteredStaff?.map((member) => (
                        <StaffCard
                          key={member?.id}
                          member={member}
                          onViewSchedule={handleViewSchedule}
                          onEditAvailability={handleEditAvailability}
                          onEditProfile={handleEditStaff}
                          onDeleteStaff={handleDeleteStaff}
                        />
                      ))}
                    </div>
                  ) : (
                    <StaffTable
                      staff={filteredStaff}
                      onViewSchedule={handleViewSchedule}
                      onEditAvailability={handleEditAvailability}
                      onEditProfile={handleEditStaff}
                      onDeleteStaff={handleDeleteStaff}
                    />
                  )}

                  {filteredStaff?.length === 0 && !staffLoading && (
                    <div className="bg-background rounded-lg border border-border p-12 text-center">
                      <Icon
                        name="Users"
                        size={48}
                        className="text-text-secondary mx-auto mb-4"
                      />
                      <h3 className="text-lg font-medium text-foreground mb-2">
                        Nu s-au găsit angajați
                      </h3>
                      <p className="text-text-secondary mb-4">
                        {searchTerm ||
                        filterRole !== "all" ||
                        filterStatus !== "all"
                          ? "Încercați să modificați filtrele de căutare"
                          : "Începeți prin a adăuga primul angajat pentru acest business"}
                      </p>
                      {!searchTerm &&
                        filterRole === "all" &&
                        filterStatus === "all" && (
                          <Button
                            variant="default"
                            onClick={handleCreateStaff}
                            className="flex items-center space-x-2"
                          >
                            <Icon name="Plus" size={16} />
                            <span>Adăugare angajat</span>
                          </Button>
                        )}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === "timeoff" && (
            <TimeOffManagement
              timeOffRequests={timeOffRequests.filter((r) => r?.type !== "day_off")}
              staffMembers={effectiveStaff}
              loading={timeOffLoading}
              error={timeOffError}
              onCreate={handleCreateTimeOff}
              onApprove={handleApproveTimeOff}
              onReject={handleRejectTimeOff}
              onViewDetails={handleViewTimeOffDetails}
            />
          )}

          {activeTab === "dayoff" && (
            <TimeOffManagement
              timeOffRequests={timeOffRequests.filter((r) => r?.type === "day_off")}
              staffMembers={effectiveStaff}
              loading={timeOffLoading}
              error={timeOffError}
              onCreate={handleCreateTimeOff}
              onApprove={handleApproveTimeOff}
              onReject={handleRejectTimeOff}
              onViewDetails={handleViewTimeOffDetails}
              defaultType="day_off"
              title="Zile libere"
              subtitle="Zile libere individuale pentru staff. Programările sunt blocate automat în zilele libere aprobate."
            />
          )}
        </div>
      </main>

      {/* Modal listă angajați (click pe "Total angajați") */}
      <OverlayListModal
        isOpen={showStaffListModal}
        title={`Angajați (${staff?.length || 0})`}
        items={(staff || []).map((m) => ({
          id: m.id,
          title: m.name || m.email || `#${m.id}`,
          subtitle: [m.role, m.status].filter(Boolean).join(" · "),
          meta: [m.email, m.phone].filter(Boolean).join(" · "),
        }))}
        onClose={() => setShowStaffListModal(false)}
        onItemClick={(it) => {
          const member = (staff || []).find((x) => String(x.id) === String(it.id));
          setShowStaffListModal(false);
          if (member) {
            setActiveTab("staff");
            setEditingStaff(member);
            setShowStaffForm(true);
          }
        }}
      />

      {/* <QuickActionButton /> */}
    </div>
  );
};

export default StaffManagement;
